import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/toast';
import { ApiException } from '@/shared/api/client';
import { createProjectSchema, type CreateProjectValues } from '@/shared/schemas/project.schema';
import { useCreateProject } from './projects.api';
import { useCustomers } from '@/features/customers/customers.api';
import { useObjects } from '@/features/objects/objects.api';

function clean<T extends Record<string, unknown>>(v: T): Record<string, unknown> {
  return Object.fromEntries(Object.entries(v).filter(([, val]) => val !== undefined && val !== ''));
}

export function ProjectFormDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateProject();
  const push = useToast((s) => s.push);
  const { data: customers } = useCustomers('');

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateProjectValues>({
    resolver: zodResolver(createProjectSchema),
    defaultValues: { customerId: '', objectId: '', title: '' },
  });

  // Kaskade: Objektliste folgt der Kundenauswahl.
  const customerId = watch('customerId');
  const { data: objects } = useObjects({ customerId: customerId || undefined, activeOnly: true });

  const onCustomerChange = (v: string) => {
    setValue('customerId', v);
    setValue('objectId', ''); // Objekt eines anderen Kunden waere ADR-010-Verstoss
  };

  const onSubmit = async (values: CreateProjectValues) => {
    try {
      await create.mutateAsync(clean(values) as CreateProjectValues);
      push('Auftrag angelegt.');
      reset();
      onClose();
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'Anlegen fehlgeschlagen.', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Neuer Auftrag">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Select
          label="Kunde *"
          {...register('customerId')}
          onChange={(e) => onCustomerChange(e.target.value)}
          error={errors.customerId?.message}
        >
          <option value="">Bitte wählen…</option>
          {customers?.items.map((c) => (
            <option key={c.id} value={c.id}>
              {c.companyName}
            </option>
          ))}
        </Select>

        <Select
          label="Objekt *"
          {...register('objectId')}
          disabled={!customerId}
          error={errors.objectId?.message}
        >
          <option value="">{customerId ? 'Bitte wählen…' : 'Zuerst Kunde wählen'}</option>
          {objects?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </Select>

        <Input label="Titel *" placeholder="z. B. Unterhaltsreinigung KW 30" {...register('title')} error={errors.title?.message} />

        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700">Vereinbarter Leistungsumfang</label>
          <textarea
            {...register('scopeOriginal')}
            rows={2}
            placeholder="Wogegen werden Zusatzleistungen abgegrenzt?"
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <p className="text-xs text-slate-500">
            Wichtig: Daran erkennt das Büro später, was „zusätzlich" war.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input label="Geplanter Beginn" type="date" {...register('plannedStart')} />
          <Input label="Geplantes Ende" type="date" {...register('plannedEnd')} />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" loading={isSubmitting}>
            Anlegen
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
